import React, { useState } from 'react';
import { useLocation, Link, Navigate } from 'react-router-dom';
import PageSEO from '../components/PageSEO';
import GalleryLightbox, { ImageLightboxModal, ClickableWorkImage } from '../components/GalleryLightbox';
import { BRAND_ASSETS, INITIAL_GALLERIES } from '../data';

export default function NewsProjectPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, '');

  if (slug === 'smk') {
    return <SmkProjectPage />;
  }

  if (slug === 'proppen-er-taget-af') {
    return <ProppenPage />;
  }

  if (slug === 'ekstrem-situation') {
    return <EkstremSituationPage />;
  }

  if (slug === 'nicklas-gr-efter-guldet' || slug === 'regionsmester-2013') {
    return <SkillsProjectPage />;
  }

  // Redirect removed pages like stormen-egon or 13-km project cleanly to projects or home
  if (slug === 'stormen-egon' || slug === 'kloakprojekt-til-35-mio') {
    return <Navigate to="/smk" replace />;
  }

  return <SmkProjectPage />;
}

// 23. STATENS MUSEUM FOR KUNST (SMK)
export function SmkProjectPage() {
  const [lightboxState, setLightboxState] = useState({ isOpen: false, index: 0 });

  const primaryImage = "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/smk-betonstoebning-pumpe.webp";

  const allSmkImages = [
    {
      src: primaryImage,
      alt: "Betonstøbning med pumpe ved spejlbassinet ved Statens Museum for Kunst",
      caption: "Betonstøbning med pumpe ved spejlbassinet ved Statens Museum for Kunst"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/smk-bassin-cirkulaer-form.webp",
      alt: "Spejlbassinets cirkulære form under opbygning",
      caption: "Spejlbassinets cirkulære form under opbygning"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/smk-armering.webp",
      alt: "Armering til spejlbassinet ved Statens Museum for Kunst",
      caption: "Armering til spejlbassinet ved Statens Museum for Kunst"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/smk-buet-konstruktion.webp",
      alt: "Detalje af buet konstruktion og armering ved spejlbassinet",
      caption: "Detalje af buet konstruktion og armering"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/smk-medarbejdere-stoebning.webp",
      alt: "Medarbejdere under støbning af spejlbassinet",
      caption: "Medarbejdere under støbning af spejlbassinet"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/smk-buede-kantelementer.webp",
      alt: "Arbejde ved de buede kantelementer på spejlbassinet",
      caption: "Arbejde ved de buede kantelementer"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/smk-armeringsdetalje.webp",
      alt: "Detalje af armeringsarbejdet ved spejlbassinet",
      caption: "Detalje af armeringsarbejdet ved spejlbassinet"
    }
  ];

  const projectImages = allSmkImages.slice(1);

  return (
    <div className="bg-stone-100 min-h-screen">
      <PageSEO
        title="Spejlbassin ved Statens Museum for Kunst | KTS-Entreprise A/S"
        description="KTS-Entreprise stod for hele opbygningen af det store, cirkulære spejlbassin foran Statens Museum for Kunst i København."
        ogTitle="Spejlbassin ved Statens Museum for Kunst | KTS-Entreprise A/S"
        ogDescription="Opbygning af det cirkulære spejlbassin foran Statens Museum for Kunst med 80 tons armeringsjern og specialfremstillede kantelementer."
        ogImage={primaryImage}
        slug="smk"
      />

      {/* Header Banner */}
      <section className="bg-stone-900 text-white pt-36 sm:pt-44 md:pt-48 lg:pt-52 pb-12 sm:pb-16 border-b-4 border-[#0072c0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#0072c0]/20 border border-[#0072c0]/40 text-blue-200 text-xs font-heading font-bold uppercase tracking-wider mb-3 rounded-xs">
            <span>Udvalgt referenceprojekt</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black uppercase tracking-tight text-white font-heading">
            Spejlbassin ved Statens Museum for Kunst
          </h1>
          <p className="text-stone-300 text-sm sm:text-base mt-2 max-w-2xl">
            Cirkulært spejlbassin, scene og kunstinstallation foran Statens Museum for Kunst i København.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        
        {/* Primært projektbillede */}
        <div className="mb-10 border border-stone-200 bg-white p-2 rounded-xs shadow-xs overflow-hidden">
          <ClickableWorkImage
            src={primaryImage}
            alt="Betonstøbning med pumpe ved spejlbassinet ved Statens Museum for Kunst"
            className="w-full h-auto max-h-[520px] object-cover rounded-xs"
            priority={true}
            onClick={() => setLightboxState({ isOpen: true, index: 0 })}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mb-16">
          <div className="lg:col-span-8 space-y-6">
            
            {/* Indledning */}
            <div className="bg-white p-6 sm:p-8 border border-stone-200 rounded-xs shadow-xs space-y-3">
              <p className="text-stone-800 text-base sm:text-lg leading-relaxed font-medium">
                KTS-Entreprise stod for hele opbygningen af det store, cirkulære spejlbassin foran Statens Museum for Kunst i København.
              </p>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Bassinet blev etableret i forbindelse med moderniseringen af museumshaven i 2013–2014. Det er cirka 30 meter i diameter, har et areal på omkring 700 m² og rummer mere end 240 m³ vand.
              </p>
            </div>

            {/* Fundament og armering */}
            <div className="bg-white p-6 sm:p-8 border border-stone-200 rounded-xs shadow-xs space-y-3">
              <div className="border-l-4 border-[#0072c0] pl-3 mb-2">
                <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-stone-900 font-heading">
                  Fundament og armering
                </h2>
              </div>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Bassinets bund blev opbygget med et solidt fundament og armeret med cirka 80 tons jern, som blev bundet direkte på stedet. Betonen blev støbt i fire etaper og udført med fald mod midten.
              </p>
            </div>

            {/* Specialfremstillede kantelementer */}
            <div className="bg-white p-6 sm:p-8 border border-stone-200 rounded-xs shadow-xs space-y-3">
              <div className="border-l-4 border-[#0072c0] pl-3 mb-2">
                <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-stone-900 font-heading">
                  Specialfremstillede kantelementer
                </h2>
              </div>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Bassinets cirkulære kant består af specialfremstillede Gote-betonelementer, som blev leveret fra Tyskland. De tungeste elementer vejer op til otte tons, og hvert element har sin egen hældning og kurve.
              </p>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed font-medium">
                Elementerne blev placeret med en nøjagtighed på ned til tre millimeter for at danne bassinets præcise cirkel.
              </p>
            </div>

            {/* Et bassin med flere funktioner */}
            <div className="bg-white p-6 sm:p-8 border border-stone-200 rounded-xs shadow-xs space-y-3">
              <div className="border-l-4 border-[#0072c0] pl-3 mb-2">
                <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-stone-900 font-heading">
                  Et bassin med flere funktioner
                </h2>
              </div>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Bassinet fungerer som spejlbassin og har et skjult overløbssystem. Det kan tømmes og anvendes som udendørs scene eller til kunstinstallationer og arrangementer. Anlægget indeholder desuden teknik til en lodret vandstråle, som kan aktiveres ved særlige lejligheder.
              </p>
            </div>

          </div>

          {/* Right Column: Key facts */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-stone-900 text-white p-6 sm:p-8 rounded-xs border border-stone-800 space-y-4">
              <span className="text-xs font-heading font-bold uppercase tracking-wider text-[#0072c0] block">
                Projektfakta
              </span>
              <ul className="space-y-3 text-xs sm:text-sm text-stone-300">
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-ruler-combined text-[#0072c0]"></i>
                  <span>Diameter: Ca. 30 meter (700 m²)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-faucet-drip text-[#0072c0]"></i>
                  <span>Vandmængde: Over 240 m³</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-weight-hanging text-[#0072c0]"></i>
                  <span>Armering: Ca. 80 tons jern</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-cubes text-[#0072c0]"></i>
                  <span>Kantelementer: Op til 8 tons pr. stk.</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-calendar text-[#0072c0]"></i>
                  <span>Udført: 2013–2014</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Billedserie: Seks supplerende projektbilleder */}
        <div className="my-16">
          <div className="border-l-4 border-[#0072c0] pl-4 mb-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold uppercase tracking-tight text-stone-900 font-heading">
              Billeder fra opbygningen af spejlbassinet
            </h2>
            <p className="text-stone-600 text-sm sm:text-base mt-1">
              Fundament, cirkulær form, armering, kantelementer og støbning ved Statens Museum for Kunst.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {projectImages.map((img, idx) => (
              <div key={idx} className="bg-white border border-stone-200 p-2 rounded-xs shadow-xs flex flex-col justify-between">
                <div className="overflow-hidden rounded-xs bg-stone-100 aspect-4/3">
                  <ClickableWorkImage
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover"
                    onClick={() => setLightboxState({ isOpen: true, index: idx + 1 })}
                  />
                </div>
                <div className="px-2 pt-3 pb-1">
                  <p className="text-xs font-heading font-semibold text-stone-700">
                    {img.caption}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Afslutning: Knap til Betonarbejde (Afsnit 23) */}
        <div className="mt-16 text-center bg-white border border-stone-200 p-8 sm:p-12 rounded-xs shadow-xs">
          <h3 className="text-xl sm:text-2xl font-bold uppercase text-stone-900 font-heading mb-3">
            Har du et betonprojekt?
          </h3>
          <p className="text-stone-600 text-sm max-w-xl mx-auto mb-6">
            Vi udfører alle former for betonarbejde – fra udgravning og fundamenter til støbte dæk og specialkonstruktioner.
          </p>
          <Link
            to="/betonarbejde"
            className="inline-flex items-center gap-3 bg-[#0072c0] hover:bg-[#005b99] text-white font-heading font-bold uppercase tracking-wider px-8 py-4 text-xs sm:text-sm transition-colors rounded-xs shadow-xs"
          >
            <span>Læs mere om vores betonarbejde</span>
            <i className="fa-solid fa-arrow-right text-xs"></i>
          </Link>
        </div>

        {/* Lightbox for Statens Museum for Kunst */}
        <ImageLightboxModal
          isOpen={lightboxState.isOpen}
          onClose={() => setLightboxState({ ...lightboxState, isOpen: false })}
          images={allSmkImages}
          currentIndex={lightboxState.index}
          onNavigate={(newIdx) => setLightboxState({ ...lightboxState, index: newIdx })}
          title="Statens Museum for Kunst (SMK)"
        />

      </div>
    </div>
  );
}

function ProppenPage() {
  return (
    <div className="bg-stone-100 min-h-screen py-36 px-4 text-center">
      <h1 className="text-3xl font-heading font-bold uppercase text-stone-900 mb-4">
        Dæmning i Frederiksværk kanal
      </h1>
      <Link to="/entreprenerservice" className="text-[#0072c0] underline">
        Gå til Entreprenør og anlæg
      </Link>
    </div>
  );
}

function EkstremSituationPage() {
  return (
    <div className="bg-stone-100 min-h-screen py-36 px-4 text-center">
      <h1 className="text-3xl font-heading font-bold uppercase text-stone-900 mb-4">
        Klimasikring i Nordsjælland
      </h1>
      <Link to="/klimatilpasning" className="text-[#0072c0] underline">
        Gå til Klimasikring
      </Link>
    </div>
  );
}

function SkillsProjectPage() {
  return (
    <div className="bg-stone-100 min-h-screen py-36 px-4 text-center">
      <h1 className="text-3xl font-heading font-bold uppercase text-stone-900 mb-4">
        Faglig stolthed og lærlinge
      </h1>
      <Link to="/om-os" className="text-[#0072c0] underline">
        Gå til Om os
      </Link>
    </div>
  );
}
